import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { formatCurrency, formatPercent } from '../../../config/chartTheme'
import { MetricSkeleton } from '../../finance/SkeletonStates'

interface Level0OverviewProps {
  entityCode: string
  periodCode: string
  onDrill: (metricCode: string, label: string) => void
}

const PL_METRICS = ['revenue', 'cogs', 'gross_profit', 'operating_expenses', 'net_income']
const BS_METRICS = ['total_assets', 'current_assets', 'ar_balance', 'total_liabilities', 'ap_balance', 'total_equity', 'working_capital']
const KPI_METRICS = ['dso', 'dpo', 'current_ratio', 'gross_margin', 'net_margin']

function MetricRow({ m, onClick }: { m: any; onClick: () => void }) {
  const val = m?.numeric_value
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center justify-between w-full px-3 py-2.5 rounded-md',
        'hover:bg-white/[0.04] transition-colors text-left group',
      )}
    >
      <span className="text-[13px] text-[var(--text-secondary,#94A3B8)] group-hover:text-[var(--text-primary,#F0F2F5)] transition-colors">
        {m?.metric_name || 'Unknown'}
      </span>
      <span className="font-['JetBrains_Mono',monospace] text-[13px] font-semibold text-[var(--text-primary,#F0F2F5)]">
        {m?.formatted || '\u2014'}
      </span>
    </button>
  )
}

export function Level0Overview({ entityCode, periodCode, onDrill }: Level0OverviewProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['exploration-overview', entityCode, periodCode],
    queryFn: () => api.get('/metrics/financial/dashboard', {
      params: { entity_code: entityCode, period_code: periodCode || undefined },
    }).then(r => r.data),
    enabled: !!periodCode,
  })

  const metrics = data?.metrics || []
  const find = (code: string) => metrics.find((m: any) => m.metric_code === code)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="space-y-2">
            {Array.from({ length: 5 }).map((_, j) => <MetricSkeleton key={j} />)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* P&L Column */}
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-3">
            <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Income Statement
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {PL_METRICS.map(code => {
              const m = find(code)
              return m ? (
                <MetricRow key={code} m={m} onClick={() => onDrill(code, m.metric_name)} />
              ) : null
            })}
          </div>
        </div>

        {/* BS Column */}
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-3">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Balance Sheet
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {BS_METRICS.map(code => {
              const m = find(code)
              return m ? (
                <MetricRow key={code} m={m} onClick={() => onDrill(code, m.metric_name)} />
              ) : null
            })}
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Key Performance Indicators
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-white/[0.04]">
          {KPI_METRICS.map(code => {
            const m = find(code)
            if (!m) return null
            return (
              <button
                key={code}
                onClick={() => onDrill(code, m.metric_name)}
                className="text-center px-3 py-2 hover:bg-white/[0.04] transition-colors rounded"
              >
                <div className="font-['JetBrains_Mono',monospace] text-[16px] font-semibold text-[var(--text-primary,#F0F2F5)]">
                  {m.formatted}
                </div>
                <div className="text-[10px] uppercase tracking-[0.5px] text-[var(--text-muted,#64748B)] mt-1">
                  {m.metric_name}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
